import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  viewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { Position } from 'geojson';
import mapboxgl, { LngLatLike } from 'mapbox-gl';
import {
  debounceTime,
  distinctUntilChanged,
  finalize,
  fromEvent,
  map,
  of,
  switchMap,
  takeUntil,
} from 'rxjs';

import { GetPublicPlacesByFilterDto } from 'src/app/core/api/models';
import { PublicPlaceService } from 'src/app/core/api/services';
import { AppConfigService } from 'src/app/core/services/config.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import { LatLng } from 'src/app/shared/components/smart-send-map-control/smart-send-map-control.component';

export enum map_color_map {
  'light' = 'light',
  'dark' = 'dark',
}

export class SmartSendPolygon {
  public coordinate: string = '';
  public properties: any[] = [];
  getPosition(): Position[][] {
    return JSON.parse(this.coordinate) as Position[][];
  }
  setPosition(value: Position[][]) {
    this.coordinate = JSON.stringify(value);
  }
}

@Component({
    selector: 'short-links-map-control',
    templateUrl: './short-links-map-control.html',
    styleUrls: ['./short-links-map-control.scss'],
    standalone: false
})
export class ShortLinksMapControlComponent implements OnInit {
  center: LngLatLike = [51.680374, 32.661343];
  map: mapboxgl.Map = null;
  drawer: MapboxDraw;
  cursorStyle: string = '';
  DisplayPolygonFillColor = '#627BC1';
  DisplayPolygonFillOpacity = 0.5;
  DisplayPolygon: GeoJSON.FeatureCollection<GeoJSON.Polygon> = {
    features: [],
    type: 'FeatureCollection',
  };
  canDrawPolygon: true;
  polygon_to_show: any;
  searchText: string = '';
  loadingPublic: boolean = false;
  pageNumber: number = 1;
  pageSize: number = 10;
  filteredOptions: any[];
  myControl = new FormControl('');
  readonly searchInput = viewChild<ElementRef>('searchInput');
  readonly statesAutocompleteRef = viewChild<MatAutocomplete>('auto');
  readonly autocompleteTrigger = viewChild(MatAutocompleteTrigger);

  @Input('Theme') Theme: string = map_color_map.light;
  @Input('MultiPolygon') MultiPolygon: boolean = false;

  @Input('Zoom') Zoom: number = 9;
  @Output('OnSave') OnSave: EventEmitter<mapboxgl.LngLat> =
    new EventEmitter<mapboxgl.LngLat>();
  @Output('OnHelp') OnHelp: EventEmitter<object> = new EventEmitter<object>();
  @Output('OnLoad') OnLoad: EventEmitter<null> = new EventEmitter<null>();
  // @Output('OnPublicPlaceClick') OnPublicPlaceClick: EventEmitter<null> = new EventEmitter<null>();
  // @Input('ShowAddPublicPlace') ShowAddPublicPlace: boolean = false;
  @Input('ShowSaveButton') ShowSaveButton: boolean = true;
  lngLat: mapboxgl.LngLat;
  styleUrl: string;
  transformReq: any;

  constructor(
    private publicPlaceService: PublicPlaceService,
    private renderer: Renderer2
  ) {}

  ngOnInit(): void {
    this.getMapStyleUrl();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['Theme']) this.getMapStyleUrl();
  }

  //#region search on map

  ngAfterViewInit() {
    fromEvent(this.searchInput()?.nativeElement, 'input')
      .pipe(
        map((event) => event as KeyboardEvent),
        map((event) => (event.target as HTMLInputElement).value),
        map((value) => value.trim()),
        map((value) => (this.searchText = value)),
        distinctUntilChanged(),
        debounceTime(1000),
        switchMap((searchText) => this.search())
      )
      .subscribe((result: any) => {
        //TODO torbatian
        this.pageNumber = 1;
        this.pageSize = 10;
        this.filteredOptions = result;
      });
  }
  getSelectedFilterPlace(place: GetPublicPlacesByFilterDto) {
    let latLng: LatLng = {
      lat: place.latitude,
      lng: place.longitude,
    };

    //gets previous marker is exists
    let marker = document.getElementsByClassName('marker');

    //checks if marker exists already: if exists : removes previous marker
    if (marker.length > 0) {
      while (marker.length > 0) {
        marker[0].parentNode.removeChild(marker[0]);
      }
    }

    // //set zoom
    const target = {
      center: latLng,
      zoom: 17,
      bearing: 0,
      pitch: 0,
    };

    //fly to selected location
    this.map.flyTo({
      ...target,
      duration: 2500,
      essential: true,
    });
  }

  //search method
  search(): any {
    if (!this.searchText.trim()) return of([]);
    this.loadingPublic = true;

    return this.publicPlaceService
      .publicPlaceGetPublicPlacesByFilter({
        body: {
          searchText: this.searchText,
          pageNumber: this.pageNumber,
          pageSize: this.pageSize,
        },
      })
      .pipe(finalize(() => (this.loadingPublic = false)));
  }

  //infinite scroll
  onScrollDown() {
    setTimeout(() => {
      const statesAutocompleteRef = this.statesAutocompleteRef();
      const autocompleteTrigger = this.autocompleteTrigger();
      if (
        statesAutocompleteRef &&
        autocompleteTrigger &&
        statesAutocompleteRef.panel
      ) {
        fromEvent(statesAutocompleteRef.panel.nativeElement, 'scroll')
          .pipe(
            map(
              (x) => this.statesAutocompleteRef().panel.nativeElement.scrollTop
            ),
            takeUntil(autocompleteTrigger.panelClosingActions)
          )
          .subscribe((x) => {
            const scrollTop =
              this.statesAutocompleteRef().panel.nativeElement.scrollTop;
            const scrollHeight =
              this.statesAutocompleteRef().panel.nativeElement.scrollHeight;
            const elementHeight =
              this.statesAutocompleteRef().panel.nativeElement.clientHeight;
            const atBottom = scrollHeight === scrollTop + elementHeight;
            if (atBottom) {
              //this.pageNumber += 1;
              this.pageSize += 10;
              this.search().subscribe((result) => {
                if (result.length > 0) this.filteredOptions = result;
              });
            }
          });
      }
    });
  }

  //#endregion

  trackByFunc(index: number, item: any) {
    return item?.id;
  }

  // onPublicPlaceButtonClick() {
  //   this.OnPublicPlaceClick.emit(null);
  // }
  ///<summery>
  ///This method get the map theme color url
  ///</summery>
  getMapStyleUrl() {
    let lightThemeUrl;
    let darkThemeUrl;
    lightThemeUrl = AppConfigService.config.useMapIr
      ? AppConfigService.config.mapIrStyleUrl
      : AppConfigService.config.gisStyleUrl;
    darkThemeUrl = AppConfigService.config.useMapIr
      ? AppConfigService.config.mapIrStyleDarkUrl
      : AppConfigService.config.gisStyleDarkUrl;
    if (this.Theme == map_color_map.light) {
      this.styleUrl = lightThemeUrl;
    } else if (this.Theme == map_color_map.dark) this.styleUrl = darkThemeUrl;
    ConsoleService.info('this.styleUrl', this.styleUrl);
  }

  transformRequest(url: string, resourceType: any) {
    if (AppConfigService.config.useMapIr)
      // use for map.ir
      this.transformReq = {
        url: url,
        headers: {
          'x-api-key': AppConfigService.config.mapIrXApiKey,
        },
      };
    // use for gis
    else
      this.transformReq = {
        url: url,
        headers: {
          Accept:
            'image/avif,image/webp,image/apng,image/svg+xml,image/*,*/*;q=0.8',
          Origin: 'http://localhost:4200',
          'Access-Control-Request-Headers': 'access-control-allow-origin',
          'Access-Control-Request-Method': 'GET',
        },
      };
  }

  public SetCenter(center: LngLatLike) {
    this.center = center;
  }

  onMapReady($event) {
    this.map = $event;
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      // controls: {
      //   trash: true,
      //   point: true,
      // },
    });
    this.map.addControl(draw);
    this.drawer = draw;

    this.OnLoad.emit(null);

    // this.map.addSource('points', {
    //   'type': 'geojson',
    //   'data': {
    //     'type': 'FeatureCollection',
    //     'features': [

    //     ],
    //   }
    // });

    // Add a circle layer
    // this.map.addLayer({
    //   'id': 'circle',
    //   'type': 'circle',
    //   'source': 'points',
    // });

    this.map.on('click', (e) => {
      this.lngLat = e.lngLat;

      //gets previous marker is exists
      let marker = document.getElementsByClassName('marker');

      //checks if marker exists already: if exists : removes previous marker
      if (marker.length > 0) {
        while (marker.length > 0) {
          marker[0].parentNode.removeChild(marker[0]);
        }
      }

      //creates marker
      const el = this.renderer.createElement('div');
      this.renderer.setProperty(el, 'className', 'marker');
      this.renderer.setStyle(
        el,
        'background',
        'url(/assets/media/svg/location-marker.svg)'
      );
      this.renderer.setStyle(el, 'width', '40px');
      this.renderer.setStyle(el, 'height', '40px');
      this.renderer.setStyle(el, 'backgroundSize', '100%');

      // Add markers to the map.
      new mapboxgl.Marker(el).setLngLat(e.lngLat).addTo(this.map);

      //set zoom
      const target = {
        center: e.lngLat,
        zoom: 14,
        bearing: 0,
        pitch: 0,
      };

      //fly to selected location
      this.map.flyTo({
        ...target,
        duration: 2500,
        essential: true,
      });
    });
  }

  onSaveButtonClick($event) {
    this.OnSave.emit(this.lngLat);
  }

  onHelpButtonClick($event) {
    this.OnHelp.emit($event);
  }

  public setEditablePolygon(input: any) {
    this.DisplayPolygon.features = [];
    //now most put the polygon to feature of editable area

    let data = this.drawer.getAll();
    let jsonPoints: Position[][] = [];
    data.features = [
      {
        type: 'Feature',
        id: 'display-polygon',
        geometry: {
          coordinates: input,
          type: 'Polygon',
        },
        properties: [],
      },
    ];
    this.drawer.set(data);
    const bounds = new mapboxgl.LngLatBounds(input[0][0], input[0][0]);

    // Extend the 'LngLatBounds' to include every coordinate in the bounds result.
    for (const coord of input[0]) {
      bounds.extend(coord);
    }

    this.map.fitBounds(bounds, {
      padding: 20,
    });
  }
}
